/**
 * Transport for the admin operations (T-252): one POST per call to the `adminProxy` HTTP
 * router. Errors are thrown in the shape of the SDK's FunctionsError (`functions/<code>`,
 * `message`, `details`) because naraDoor.ts and the pages read them that way.
 */

import { auth } from "./firebase";

// Read at call time so a test (or the emulator switch) can change them after import.
export function adminProxyUrl(name: string): string {
  const project = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const base = import.meta.env.DEV
    ? `http://127.0.0.1:5001/${project}/us-east4`
    : `https://us-east4-${project}.cloudfunctions.net`;
  return `${base}/adminProxy/${name}`;
}

const proxyError = (code: string, message: string, details: unknown = null) =>
  Object.assign(new Error(message), { code: `functions/${code}`, details });

function parseJson(text: string): { ok: true; value: unknown } | { ok: false } {
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch {
    return { ok: false };
  }
}

export async function callAdminProxy<T, R>(name: string, data?: T): Promise<{ data: R }> {
  const token = await auth.currentUser?.getIdToken();
  let response: Response;
  try {
    response = await fetch(adminProxyUrl(name), {
      method: "POST",
      headers: { ...(token && { Authorization: `Bearer ${token}` }), "Content-Type": "application/json" },
      body: JSON.stringify(data ?? {}),
    });
  } catch {
    throw proxyError("unavailable", "Could not reach the server");
  }

  const text = await response.text();
  // An op with no result (a delete) may answer 200 with an empty body: that is a null result.
  const body = parseJson(response.ok && text === "" ? "null" : text);
  if (!body.ok) throw proxyError("internal", "Unexpected server response");
  if (response.ok) return { data: body.value as R };

  const envelope = (body.value as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
  if (!envelope?.code) throw proxyError("internal", "Unexpected server response");
  throw proxyError(envelope.code, envelope.message ?? "", envelope.details ?? null);
}
